// generate buffer of length filled with repeated byte
function bufOf(len: number, byte: number) {
  const buf = new Uint8Array(len);
  for (let i = 0; i < buf.length; i++) {
    buf[i] = byte;
  }
  return buf;
}

// concatenate buffers
function join(...bufs: Uint8Array[]): Uint8Array {
  const len: number = bufs.reduce((len, buf) => len + buf.length, 0);
  const buf = new Uint8Array(len);

  let p = 0;
  for (let i = 0; i < bufs.length; i++) {
    buf.set(bufs[i], p);
    p += bufs[i].length;
  }

  return buf;
}

// xor buffer b over buffer a
function xor(a: Uint8Array, b: Uint8Array): Uint8Array {
  for (let i = 0, l = Math.min(a.length, b.length); i < l; i++) {
    a[i] ^= b[i];
  }
  return a;
}

// convert string to buffer
function str2buf(str: string): Uint8Array {
  const buf = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) {
    buf[i] = str.charCodeAt(i) & 0xff;
  }
  return buf;
}

export type HashAlgorithm = "SHA-1" | "SHA-256" | "SHA-384" | "SHA-512";

// maps hash algorithm to
const blockSizeTable: Record<HashAlgorithm, number> = {
  "SHA-1": 64,
  "SHA-256": 64,
  "SHA-384": 128,
  "SHA-512": 128,
};

// hash buffer
async function hash(
  algorithm: HashAlgorithm,
  buffer: Uint8Array
): Promise<Uint8Array> {
  type BufferSource = Parameters<(typeof crypto)["subtle"]["digest"]>[1];

  return new Uint8Array(
    await crypto.subtle.digest(algorithm, buffer as BufferSource)
  );
}

// create Hmac algorithm
export async function createHmac(algorithm: HashAlgorithm, key: string) {
  const b = blockSizeTable[algorithm];

  let keyBuf = str2buf(key);
  if (keyBuf.length > b) {
    keyBuf = await hash(algorithm, keyBuf);
  }

  const ipad = xor(bufOf(b, 0x36), keyBuf);
  const opad = xor(bufOf(b, 0x5c), keyBuf);

  const buffers: Uint8Array[] = [];

  const itf = {
    update(buf: ArrayBuffer | Uint8Array | string) {
      buffers.push(
        typeof buf === "string"
          ? str2buf(buf)
          : buf instanceof ArrayBuffer
            ? new Uint8Array(buf)
            : buf
      );
      return itf;
    },
    async digest() {
      return await hash(
        algorithm,
        join(opad, await hash(algorithm, join(ipad, ...buffers)))
      );
    },
  };

  return itf;
}
